import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { isArray, isString, omitBy } from "lodash";
import { Observable } from "rxjs";
import {
  DrugPrescription,
  DrugPrescriptionCreate
} from "src/app/backend/models/prescription/drug-prescription";
import {
  Prescription,
  PrescriptionCreate
} from "src/app/backend/models/prescription/prescription";
import { UserNotify, UserObserve } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { sntTranslate } from "src/app/support/translate";
import { environment } from "src/environments/environment";
import { isUndefined } from "lodash-es";
import { NullablePartial } from "src/app/support/nullable-partial";

@Injectable()
export class PrescriptionBackend {
  constructor(private http: HttpClient) {}

  @UserNotify({
    successMessage: marker("Ricetta creata"),
    errorMessage: marker("Impossibile creare la ricetta")
  })
  create(payload: PrescriptionCreate): Observable<Prescription> {
    return this.http.post<Prescription>(
      `${environment.snoots.api.healthcare}/prescriptions`,
      payload
    );
  }

  load(id: string): Observable<Prescription> {
    return this.http.get<Prescription>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}`
    );
  }

  loadLight(id: string): Observable<Prescription> {
    return this.http.get<Prescription>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  listByPatient(
    patientId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Prescription>> {
    return this.http.get<PaginatedResult<Prescription>>(
      `${environment.snoots.api.healthcare}/prescriptions`,
      {
        params: {
          patientId: patientId,
          p: request.page,
          s: request.pageSize
        }
      }
    );
  }

  list(request: PageRequest): Observable<PaginatedResult<Prescription>> {
    return this.http.get<PaginatedResult<Prescription>>(
      `${environment.snoots.api.healthcare}/prescriptions`,
      {
        params: {
          p: request.page,
          s: request.pageSize
        }
      }
    );
  }

  @UserNotify({
    successMessage: marker("Farmaco aggiunto"),
    errorMessage: marker("Impossibile aggiungere il farmaco selezionato")
  })
  add(
    id: string,
    request: Partial<DrugPrescriptionCreate>
  ): Observable<Prescription> {
    return this.http.post<Prescription>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}/drugs`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Farmaco aggiornato"),
    errorMessage: marker("Impossibile aggiornare il farmaco")
  })
  updateDrug(
    id: string,
    drugId: string,
    request: Partial<DrugPrescription>
  ): Observable<Prescription> {
    return this.http.patch<Prescription>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}/drugs/${drugId}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Farmaco rimosso"),
    errorMessage: marker("Impossibile rimuovere il farmaco selezionato")
  })
  removeDrug(id: string, drugId: string): Observable<Prescription> {
    return this.http.delete<Prescription>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}/drugs/${drugId}`
    );
  }

  private updateSilently(
    id: string,
    request: NullablePartial<Partial<Prescription>>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Ricetta aggiornata"),
    errorMessage: marker("Impossibile aggiornare la ricetta")
  })
  update(id: string, request: Partial<Prescription>): Observable<void> {
    return this.updateSilently(id, request);
  }

  @UserNotify({
    successMessage: marker("Cliente aggiornato"),
    errorMessage: marker("Impossibile aggiornare il cliente")
  })
  updateCustomer(
    id: string,
    request: NullablePartial<Pick<Prescription, "customer" | "patient">>
  ): Observable<void> {
    return this.updateSilently(id, request);
  }

  @UserNotify({
    successMessage: marker("Medico aggiornato"),
    errorMessage: marker("Impossibile aggiornare il medico")
  })
  updatePhysician(
    id: string,
    request: Pick<Prescription, "physician">
  ): Observable<void> {
    return this.updateSilently(id, request);
  }

  @UserNotify({
    successMessage: marker("Diagnosi aggiornata"),
    errorMessage: marker("Impossibile aggiornare la diagnosi")
  })
  updateDiagnosis(
    id: string,
    request: Pick<Prescription, "diagnosis">
  ): Observable<void> {
    return this.updateSilently(id, request);
  }

  @UserNotify({
    successMessage: marker("Note aggiornate"),
    errorMessage: marker("Impossibile aggiornare le note")
  })
  updateNote(
    id: string,
    request: Pick<Prescription, "note">
  ): Observable<void> {
    return this.updateSilently(id, request);
  }

  @UserNotify({
    successMessage: marker("Paziente aggiornato"),
    errorMessage: marker("Impossibile aggiornare il paziente")
  })
  updatePatient(
    id: string,
    request: Pick<Prescription, "patient">
  ): Observable<void> {
    return this.updateSilently(id, request);
  }

  @UserNotify({
    successMessage: marker("Codice struttura aggiornato"),
    errorMessage: marker("Impossibile aggiornare il codice struttura")
  })
  updateAccountCode(
    id: string,
    request: Pick<Prescription, "accountCode">
  ): Observable<void> {
    return this.updateSilently(id, request);
  }

  @UserNotify({
    successMessage: marker("Ricetta eliminata"),
    errorMessage: marker("Impossibile eliminare la ricetta")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Ricetta emessa"),
    errorMessage: (httpErrorResponse: HttpErrorResponse) => {
      if (!httpErrorResponse.error) {
        return sntTranslate(marker("Impossibile emettere la ricetta"));
      }

      if (isString(httpErrorResponse.error)) {
        return httpErrorResponse.error;
      }

      if (isArray(httpErrorResponse.error)) {
        return (<string[]>httpErrorResponse.error).join(", ");
      }

      return sntTranslate(marker("Impossibile emettere la ricetta"));
    }
  })
  emit(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}/emit`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Credenziali aggiornate"),
    errorMessage: marker("Impossibile aggiornare le credenziali")
  })
  doctor({
    memberId,
    username,
    password,
    admin
  }: {
    memberId: string;
    username: string;
    password: string;
    admin?: boolean;
  }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/rev/doctors/${memberId}`,
      omitBy(
        {
          username,
          password,
          admin
        },
        isUndefined
      )
    );
  }

  @UserNotify({
    successMessage: marker("Ricetta clonata"),
    errorMessage: marker("Impossibile clonare la ricetta")
  })
  clone(id: string): Observable<Prescription> {
    return this.http.post<Prescription>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}/clones`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Ricetta annullata"),
    errorMessage: marker("Impossibile annullare la ricetta")
  })
  cancel(id: string, revNumber: string): Observable<Prescription> {
    return this.http.put<Prescription>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}/cancel`,
      {}
    );
  }

  @UserObserve({
    loading: marker("Invio ricetta"),
    success: marker("Ricetta inviata"),
    error: marker("Impossibile inviare la ricetta")
  })
  send(
    id: string,
    options: {
      email: string;
      attachments: Array<{
        contentType: string;
        fileName: string;
        storageId: string;
      }>;
      note?: string;
    }
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/prescriptions/${id}/send`,
      omitBy(
        {
          email: options.email,
          attachments: options.attachments,
          notes: options.note
        },
        isUndefined
      )
    );
  }
}
